import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Divider,
    CircularProgress,
    Autocomplete
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { t } from 'i18next';
import {
    createLearningPost,
    createLearningTag,
    getLearningMedia,
    getLearningTags
} from '../../../services/aprender';
import MediaGallery from './components/MediaGallery';

const AprenderCriar = () => {
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const [content, setContent] = useState('');
    const [uploadedMedia, setUploadedMedia] = useState(null);
    const [availableMedia, setAvailableMedia] = useState([]);
    const [selectedExistingMedia, setSelectedExistingMedia] = useState([]);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchTags = async () => {
            setLoadingTags(true);
            try {
                const tagsFromApi = await getLearningTags();
                setAvailableTags(tagsFromApi);
            } catch (err) {
                console.error('Erro a procurar tags:', err);
                setAvailableTags([]);
            } finally {
                setLoadingTags(false);
            }
        };

        const fetchMedia = async () => {
            setLoadingMedia(true);
            try {
                const data = await getLearningMedia();
                console.log(data);
                setAvailableMedia(data);
            } catch (err) {
                console.error('Erro ao obter media:', err);
                setAvailableMedia([]);
            } finally {
                setLoadingMedia(false);
            }
        };

        fetchTags();
        fetchMedia();
    }, []);

    const handleCreate = async () => {
        let newErrors = {};
        if (!title) newErrors.title = t('learning.fill_title');
        if (!description) newErrors.description = t('learning.fill_description');
        if (!content || content === '<p><br></p>')
            newErrors.content = t('learning.add_content');
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        const payload = {
            title,
            description,
            format: 'HTML',
            content,
            featured_media: uploadedMedia?.uuid || null,
            media: selectedExistingMedia.map((m) => m.uuid),
            tags: tags.map((t) => t.uuid)
        };

        setLoading(true);
        try {
            const response = await createLearningPost(payload);
            alert(t('learning.created_success'));
            navigate('/aprender');
        } catch (error) {
            console.error('Erro ao criar conteúdo:', error);
            if (error.response) {
                alert(
                    t('learning.error_server', {
                        status: error.response.status,
                        description:
                            error.response.data?.description ||
                            'Ocorreu um erro no servidor.'
                    })
                );
            } else {
                alert(t('learning.error_api'));
            }
        } finally {
            setLoading(false);
        }
    };

    const modules = {
        toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image', 'video'],
            ['clean']
        ]
    };

    return (
        <>
            <style>
                {`
                  .ql-editor img {
                    max-width: 100%;
                    height: auto;
                    display: block;
                    margin: 10px auto;
                    border-radius: 8px;
                  }
                `}
            </style>
            <Box sx={{ p: 3 }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={3}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/aprender')}
                    >
                        {t('learning.back')}
                    </Button>
                    <Typography variant="h4" fontWeight="700">
                        {t('learning.create_new')}
                    </Typography>
                    <Box sx={{ width: 48 }} />
                </Box>

                <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: 1, p: 2 }}>
                    <CardContent>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('learning.title2')}
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    error={!!errors.title}
                                    helperText={errors.title}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('learning.description')}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    error={!!errors.description}
                                    helperText={errors.description}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    gutterBottom
                                >
                                    {t('learning.media')}
                                </Typography>
                                {loadingMedia ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <MediaGallery
                                        availableMedia={availableMedia}
                                        uploadedMedia={uploadedMedia}
                                        setUploadedMedia={setUploadedMedia}
                                        selectedExistingMedia={selectedExistingMedia}
                                        setSelectedExistingMedia={
                                            setSelectedExistingMedia
                                        }
                                        setContent={setContent}
                                    />
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    gutterBottom
                                >
                                    Tags
                                </Typography>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={availableTags.map((tag) => ({
                                        label: tag.tag,
                                        uuid: tag.uuid
                                    }))}
                                    getOptionLabel={(option) => option.label || option}
                                    value={tags.map((tag) => ({
                                        label: tag.tag,
                                        uuid: tag.uuid
                                    }))}
                                    onChange={async (event, newValue) => {
                                        const updatedTags = [];

                                        for (let item of newValue) {
                                            let name, uuid;
                                            if (typeof item === 'string') {
                                                name = item;
                                                try {
                                                    const newTag =
                                                        await createLearningTag(name);
                                                    uuid = newTag.uuid;
                                                    setAvailableTags((prev) => [
                                                        ...prev,
                                                        newTag
                                                    ]);
                                                } catch (err) {
                                                    console.error(
                                                        'Erro ao criar tag:',
                                                        err
                                                    );
                                                    continue;
                                                }
                                            } else {
                                                name = item.label;
                                                uuid = item.uuid;
                                            }
                                            updatedTags.push({ tag: name, uuid });
                                        }

                                        setTags(updatedTags);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={t('learning.tags_placeholder')}
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    gutterBottom
                                >
                                    {t('learning.content')}
                                </Typography>
                                <ReactQuill
                                    modules={modules}
                                    theme="snow"
                                    value={content}
                                    onChange={setContent}
                                    placeholder={t('learning.content_placeholder')}
                                    style={{ height: '300px', marginBottom: '60px' }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const imageUrl =
                                            e.dataTransfer.getData('text/plain');
                                        if (imageUrl && imageUrl.startsWith('http')) {
                                            const quill =
                                                e.target.closest(
                                                    '.ql-container'
                                                )?.__quill;
                                            if (quill) {
                                                const range = quill.getSelection(true);
                                                quill.insertEmbed(
                                                    range.index,
                                                    'image',
                                                    imageUrl
                                                );
                                            }
                                        }
                                    }}
                                />
                                {errors.content && (
                                    <Typography color="error" variant="body2" mt={1}>
                                        {errors.content}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        p={2}
                    >
                        <Button variant="outlined" onClick={() => navigate('/aprender')}>
                            {t('learning.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreate}
                            disabled={loading}
                        >
                            {loading ? t('learning.creating') : t('learning.create')}
                        </Button>
                    </Box>
                </Card>
            </Box>
        </>
    );
};

export default AprenderCriar;
